import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { ProductDefinition } from "../features/lazertrader/types";
import { useListedAlgo } from "../features/lazertrader/contexts/listAlgosContext";
import { ProductType, productTypeToList } from "./nats/contracts";
import { useSearch, useNavigate } from "@tanstack/react-router";
import { Price } from "../price";
import { indexRoute } from "../routes";

export type BookSide = "bid" | "ask";

export type Prefill = {
  direction: string;
  clickedSide: BookSide;
  grid: string;
  gridOptions: string[];
  contractsByArea: Record<string, number>;
  /**
   * Day-ahead spot per member area, keyed like {@link contractsByArea}: coupled
   * areas can clear at different prices, so the cluster's one spot cell is not
   * necessarily the spot of the area the ticket opens on. Prefills the
   * reference price of a StepShimurai or LinearCloser.
   */
  spotByArea: Record<string, Price | undefined>;
  productType: string;
  product: string;
  deliveryStart: string;
  deliveryEnd: string;
  price: Price | undefined;
  product_type: ProductType[];
};

export type PrefillInput = Omit<Prefill, "product_type">;

export type SelectedContract = {
  contractId: number;
  areaCode: string;
  side: BookSide;
};

interface PrefillContextProps {
  prefill?: Prefill;
  updatePrefill: (input: PrefillInput) => void;
  reset: () => void;
  selectedEic: string;
  setSelectedEic: (eic: string) => void;
  selectedProducts: ProductDefinition[];
  selectedContract?: SelectedContract;
  selectContractArea: (areaCode: string) => void;
}

const prefillContext = createContext<PrefillContextProps | undefined>(
  undefined
);

export const PrefillProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { selectedAlgos } = useListedAlgo();
  const [prefill, setConfigPrefill] = useState<Prefill | undefined>(undefined);
  const { orderbookGridId } = useSearch({
    from: indexRoute.id,
  });

  const navigate = useNavigate({ from: indexRoute.id });

  const [selectedEic, setSelectedEicBase] = useState<string>(
    orderbookGridId ?? "3"
  );

  const [selectedContract, setSelectedContract] = useState<
    SelectedContract | undefined
  >(undefined);

  const updatePrefill = useCallback((input: PrefillInput) => {
    setConfigPrefill({ ...input, product_type: productTypeToList() });

    const contractId = input.contractsByArea[input.grid];
    setSelectedContract(
      contractId === undefined
        ? undefined
        : { contractId, areaCode: input.grid, side: input.clickedSide }
    );
  }, []);

  const selectContractArea = useCallback(
    (areaCode: string) => {
      if (prefill === undefined) {
        return;
      }
      const contractId = prefill.contractsByArea[areaCode];
      if (contractId === undefined) {
        return;
      }
      setSelectedContract((current) => ({
        contractId,
        areaCode,
        side: current?.side ?? prefill.clickedSide,
      }));
    },
    [prefill]
  );

  const reset = useCallback(() => {
    setConfigPrefill(undefined);
    setSelectedContract(undefined);
  }, []);

  const setSelectedEic = useCallback(
    (eic: string) => {
      reset();
      setSelectedEicBase(eic);
      navigate({ search: (old) => ({ ...old, orderbookGridId: eic }) });
    },
    [navigate, reset]
  );

  const selectedProducts = useMemo(() => {
    let selectedProducts: ProductDefinition[] = [];
    if (prefill !== undefined) {
      const value = {
        delivery_start: prefill.deliveryStart,
        delivery_end: prefill.deliveryEnd,
        delivery_area_id: prefill.grid,
        product_types: prefill.product_type,
      };
      selectedProducts = [value];
    } else {
      selectedProducts = selectedAlgos.map(
        (selectedAlgo) => selectedAlgo.product_definitions[0]
      );
    }
    return selectedProducts;
  }, [selectedAlgos, prefill]);

  return (
    <prefillContext.Provider
      value={{
        updatePrefill,
        prefill,
        reset,
        selectedEic,
        setSelectedEic,
        selectedProducts,
        selectedContract,
        selectContractArea,
      }}
    >
      {children}
    </prefillContext.Provider>
  );
};

export const usePrefillContext = () => {
  const context = useContext(prefillContext);
  if (!context) {
    throw new Error(
      "usefprefillContext must be used within a PrefillContextProvider"
    );
  }
  return context;
};
